import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PageHeader from '../../../Common/PageHeader';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import Select from 'react-select';
import { blogService } from '../../../api/blog/blogService';
import { DeleteModal } from '../../../Common/DeleteModal';
import { getImageUrl } from '../../../utils/imageUrl';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Yet to Publish', label: 'Yet to Publish' },
  { value: 'Live', label: 'Live' },
];

const BlogsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState(CATEGORY_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [blogs, setBlogs] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  useEffect(() => {
    // Fetch categories for filter
    import('../../../api/blog/blogCategoryService').then(({ blogCategoryService }) => {
      blogCategoryService.getAllCategories().then((res: any) => {
        console.log('Categories API response:', res);
        let catList = [];
        if (Array.isArray(res)) {
          catList = res;
        } else if (Array.isArray(res.data?.paginatedResults)) {
          catList = res.data.paginatedResults;
        } else if (Array.isArray(res.data)) {
          catList = res.data;
        } else if (Array.isArray(res.data?.data)) {
          catList = res.data.data;
        }
        const mappedCategories = catList.map((cat: any) => ({ value: cat._id, label: cat.category || cat.name }));
        console.log('Mapped categories:', mappedCategories);
        setCategories(mappedCategories);
      });
    });
  }, []);

  const CATEGORY_FILTER_OPTIONS = useMemo(() => {
    return [
      { value: 'all', label: 'All Categories' },
      ...categories
    ];
  }, [categories]);

  useEffect(() => {
    fetchBlogs();
  }, [page, pageSize, categoryFilter, statusFilter]);

  useEffect(() => {
    console.log('Category filter changed, current value:', categoryFilter);
    console.log('Available category options:', CATEGORY_FILTER_OPTIONS);
  }, [categoryFilter, CATEGORY_FILTER_OPTIONS]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.trim()) {
        setPage(1);
        fetchBlogs();
      } else {
        fetchBlogs();
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params: any = { 
        page, 
        pageSize, 
        search
      };
      
      // Only add category filter if it's not 'all'
      if (categoryFilter.value !== 'all') {
        params.blogCategory = categoryFilter.value;
      }
      
      // Only add status filter if it's not 'all'
      if (statusFilter.value !== 'all') {
        if (statusFilter.value === 'Live') {
          params.isActive = true;
        } else if (statusFilter.value === 'Yet to Publish') {
          params.isActive = false;
        }
      }
      
      console.log('Fetching blogs with params:', params);
      const res = await blogService.getAllBlogs(params);
      console.log('Blog API response:', res);
      let blogList = [];
      if (Array.isArray(res)) {
        blogList = res;
        setTotalPages(Math.ceil(blogList.length / pageSize) || 1);
        setTotalCount(blogList.length);
      } else if (Array.isArray(res.data?.paginatedResults)) {
        blogList = res.data.paginatedResults;
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
      } else if (Array.isArray(res.data)) {
        blogList = res.data;
        setTotalPages(Math.ceil(blogList.length / pageSize) || 1);
        setTotalCount(blogList.length);
      } else if (Array.isArray(res.data?.data)) {
        blogList = res.data.data;
        setTotalPages(Math.ceil(blogList.length / pageSize) || 1);
        setTotalCount(blogList.length);
      } else if (res.data?.paginatedResults) {
        blogList = res.data.paginatedResults;
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
      }
      console.log('Filtered blog list:', blogList);
      if (blogList.length > 0) {
        console.log('Sample blog data structure:', blogList[0]);
        console.log('Sample blog category:', blogList[0].blogCategory);
      }
      setBlogs(blogList);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlog = () => {
    navigate('/blog-details/blogs/add');
  };

  const handleView = (blog: any) => {
    navigate(`/blog-details/blogs/${blog._id}`, { state: { blog } });
  };

  const handleCategoryFilter = (val: any) => {
    console.log('Category filter changed:', val);
    setCategoryFilter(val || { value: 'all', label: 'All Categories' });
    setPage(1);
  };
  
  const handleStatusFilter = (val: any) => {
    setStatusFilter(val || STATUS_OPTIONS[0]);
    setPage(1);
  };

  const handleDelete = async (blogId: string, blogTitle: string) => {
    setDeleteTarget({ id: blogId, title: blogTitle });
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    if (deleteTarget && deleteTarget.id) {
      try {
        await blogService.deleteBlog(deleteTarget.id);
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };
  
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleClearSearch = () => {
    setSearch('');
    searchInputRef.current?.focus();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  if (loading) {
    return (
      <div className="container-fluid" style={{ marginTop: '20px' }}>
        <Breadcrumbs items={[{ label: 'Blog Details' }, { label: 'Blogs', active: true }]} />
        <PageHeader
          breadcrumb={null}
          title="Blogs Management"
          addNewLabel="Add Blog"
          onAddNew={handleAddBlog}
        />
        <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
          <div className="filter-row">
            <div className="filter-controls">
              <div style={{ width: '240px', height: '38px' }} className="shimmer-base" />
            </div>
          </div>
          <ShimmerLoader type="table" rows={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <Breadcrumbs items={[{ label: 'Blog Details' }, { label: 'Blogs', active: true }]} />
      <PageHeader
        breadcrumb={null}
        title="Blogs Management"
        addNewLabel="Add Blog"
        onAddNew={handleAddBlog}
      />
      <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
        <div className="filter-row">
          <div className="filter-controls" style={{ gap: 12 }}>
            <div style={{ position: 'relative', width: 240 }}>
              <input
                type="text"
                className="form-search"
                placeholder="Search by title"
                style={{ width: 240, paddingRight: 32 }}
                value={search}
                onChange={handleSearchChange}
                ref={searchInputRef}
              />
              {search && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    zIndex: 2
                  }}
                  aria-label="Clear search"
                >
                  <span style={{ fontSize: 18, color: '#888' }}>&times;</span>
                </button>
              )}
            </div>
            <div style={{ minWidth: 180 }}>
              <Select
                classNamePrefix="react-select"
                value={categoryFilter}
                onChange={handleCategoryFilter}
                options={CATEGORY_FILTER_OPTIONS}
                placeholder="Category"
                isSearchable={true}
                isClearable={categoryFilter.value !== 'all'}
                maxMenuHeight={400}
                menuPosition="fixed"
                styles={{ 
                  control: base => ({ 
                    ...base, 
                    minHeight: '38px', 
                    borderRadius: 8, 
                    fontSize: 14 
                  }),
                  menu: base => ({ 
                    ...base, 
                    maxHeight: 400,
                    zIndex: 9999,
                    overflow: 'hidden',
                    position: 'absolute'
                  }),
                  menuList: base => ({ 
                    ...base, 
                    maxHeight: 400,
                    padding: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }),
                  option: (base, state) => ({ 
                    ...base, 
                    padding: '10px 12px',
                    backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                    color: state.isSelected ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: '1.2',
                    '&:hover': {
                      backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                    }
                  }),
                  input: base => ({ 
                    ...base, 
                    margin: 0,
                    padding: 0 
                  }),
                  placeholder: base => ({ 
                    ...base, 
                    color: '#6c757d' 
                  }),
                  singleValue: base => ({
                    ...base,
                    color: '#333'
                  })
                }}
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <Select
                classNamePrefix="react-select"
                value={statusFilter}
                onChange={handleStatusFilter}
                options={STATUS_OPTIONS}
                placeholder="Status"
                isSearchable={false}
                isClearable={statusFilter.value !== 'all'}
                styles={{ control: base => ({ ...base, minHeight: '38px', borderRadius: 8, fontSize: 14 }) }}
              />
            </div>
          </div>
        </div>
        <table className="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Category</th>
              <th>Publish Date</th>
              <th>Blog Image</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.map((blog: any, idx: number) => (
              <tr key={blog._id} className="clickable-row" style={{ cursor: 'pointer' }} onClick={() => handleView(blog)}>
                <td>{(page - 1) * pageSize + idx + 1}</td>
                <td>{blog.title}</td>
                <td>
                  {blog.blogCategory && typeof blog.blogCategory === 'object'
                    ? blog.blogCategory.category
                    : blog.category || blog.blogCategory || '-'}
                </td>
                <td>{blog.publishedDate ? blog.publishedDate.slice(0, 10) : ''}</td>
                <td>
                  {blog.image ? (
                    <img
                      src={getImageUrl(blog.image)}
                      alt="blog"
                      style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : '-'}
                </td>
                <td>
                  {(() => {
                    const isFuture = new Date(blog.publishedDate) > new Date();
                    const displayStatus = isFuture ? 'Yet to Publish' : blog.isActive ? 'Live' : 'Yet to Publish';
                    return (
                      <span className={`status-badge ${displayStatus === 'Live' ? 'live' : displayStatus === 'Yet to Publish' ? 'inactive' : 'draft'}`}>{displayStatus}</span>
                    );
                  })()}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <button
                      className="icon-btn view-btn"
                      onClick={e => { e.stopPropagation(); handleView(blog); }}
                      title="View"
                      type="button"
                    >
                      <span className="bi bi-eye" style={{ fontSize: 18 }}></span>
                    </button>
                    <button
                      className="icon-btn edit-btn"
                      onClick={e => { e.stopPropagation(); handleView(blog); }}
                      title="Edit"
                      type="button"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn delete-btn"
                      onClick={e => { e.stopPropagation(); handleDelete(blog._id, blog.title); }}
                      title="Delete"
                      type="button"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {blogs.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center">No blogs found.</td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination UI */}
        {totalPages > 1 && (
          <div
            className="d-flex pagination-row align-items-center mt-3"
            style={{
              width: 1160,
              height: 52,
              justifyContent: 'space-between',
              borderRadius: '8px',
              paddingTop: 8,
              paddingRight: 12,
              paddingBottom: 8,
              paddingLeft: 12,
            }}
          >
            <div
              style={{
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: 0,
              }}
            >
              Showing results
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="mx-2"
                style={{
                  width: 70,
                  height: 36,
                  borderRadius: 8,
                  padding: 8,
                  gap: 8,
                  borderWidth: 0.5,
                  borderStyle: 'solid',
                  borderColor: '#888',
                  fontFamily: 'Helvetica Neue, Arial, sans-serif',
                  fontSize: '12px',
                }}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              of {totalCount}
            </div>
            <div className="d-flex align-items-center">
              Page
              <button className="btn btn-light mx-1" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&lt;</button>
              <span className="btn btn-primary mx-1 disabled">{page}</span>
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1 + page).map(p => (
                p <= totalPages ? (
                  <button key={p} className="btn btn-light mx-1" onClick={() => handlePageChange(p)}>{p}</button>
                ) : null
              ))}
              {page < totalPages - 2 && <span className="mx-1">...</span>}
              <button className="btn btn-light mx-1" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&gt;</button>
              <span className="mx-2">of {totalPages}</span>
            </div>
          </div>
        )}
      </div>
      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Blog"
        message={deleteTarget ? `Are you sure you want to delete the blog titled: "${deleteTarget.title}"? This action cannot be undone.` : ''}
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </div>
  );
};

export default BlogsPage; 